import {Location, History} from "history";
import {animated} from "react-spring";
import useUnifiedCardContainer, {UnifiedCardContainerOpts} from "./useUnifiedCardContainer";
import {Box, Col, css, DSIconButton, DSIconMedia} from "@cdx/ds";
import useBling from "../../lib/hooks/useBling";
import {
  hasPermissionToBookmarkCard,
  hasPermissionToGuardCard,
  hasPermissionToModifyCard,
  hasPermissionToWatchCard,
} from "../../lib/permissions";
import {useNotifications} from "../notifications/useNotifications";
import {
  ArchiveOrDeleteBottomButton,
  AttachmentPanelButton,
  BookmarkBottomButton,
  ChildCardPanelButton,
  ConvoPanelButton,
  DocCardBottomButton,
  HistoryPanelButton,
  PropPanelButton,
  SubscribeBottomButton,
} from "./SidePanelButtons";
import {getChildCardInfo, getParentCard, hasChildCards} from "../workflows/workflow-utils";
import {
  EditCardContainerContent,
  ViewCardContainerContent,
  openSidePanel,
} from "./card-container-utils";
import PropEdit from "../../components/PropEdit";
import {dayToDateStr} from "../../lib/date-utils";
import {api} from "../../lib/api";
import RawAttachments from "./sidebars/attachments";
import Uploader, {useUpload} from "../../components/uploader";
import {useFilePaster} from "./FilePasteHandler";
import {Dispatch, ReactNode, SetStateAction, lazy, useEffect, useRef, useState} from "react";
import {getStatusForCard} from "../../lib/card-status-utils";
import CardDetailFrame, {
  CardDetailFrameAboveBottom as RawCardDetailFrameAboveBottom,
} from "./CardDetailFrame";
import {ArrowOverlay, cx, DropDownForChild, TooltipForChild, uiStyles} from "@cdx/common";
import dsStyles from "@cdx/ds/css/index.css";
import {cardContainerStyles} from "./unified-card-container.css";
import {NonPublicTopArea, PublicTopArea} from "./OpenCardTopArea";
import {DropChildCardArea} from "./DropChildCardArea";
import Workflow from "./workflow";
import useFocusMode, {FocusProps} from "./useFocusMode";
import {createPersistStore} from "../../lib/hooks/usePersistStore";
import {isSelectionActive} from "../../lib/utils";
import {CardParentPill} from "./CardParentPill";
import CardEmbeds from "./CardEmbeds";
import {OpenCardStatusRow} from "./OpenCardStatusRow";
import {moveCardsToDeck} from "../../lib/cross-project-cards-or-decks";
import {useImageViewerInfo} from "../image-viewer/ImageViewContext";
import {useLocalStorageState} from "../../lib/storage";
import {canDisplayAsImage} from "../../lib/file-utils";
import {usePropOnHoverChange} from "../../components/Card/useHoverShortcuts";
import messenger from "../../lib/messenger";
import {
  SelectContent,
  lexicalSerializer,
} from "../../components/RichTextarea/Lexical/LexicalRichTextProvider";
import {useDoneAnim} from "../card-animations/card-animation-utils";
import {waitForResult} from "../../lib/wait-for-result";
import {Card} from "../../cdx-models/Card";
import {Root} from "../../cdx-models/Root";
import {ArenaCtx} from "../card-panel/card-panel-utils";
import {CardOrder} from "../card-order/useCardOrder";
import Conversations from "./sidebars/conversations/Conversations";
import {
  CoverInfo,
  getCoverFileWithParent,
  getCoverInfoWithParent,
} from "../../components/Card/card-cover-utils";
import {CdxCropImg} from "../../components/CdxImg";
import CoverImageOptions, {CoverFileModifier} from "./sidebars/CoverImageOptions";
import {FileId} from "../../cdx-models/File";
import {useBigCardAnimation} from "../../components/Card/useMiniCardAnimation";

export const cardEditPersistStore = createPersistStore({
  elementToKey: (item: Card) => {
    const id = item.$meta.get("cardId", null);
    return id ? `edit-card-${id}` : "";
  },
});

const Attachments = RawAttachments as any;
const CardDetailFrameAboveBottom = RawCardDetailFrameAboveBottom as any;
const ShowUploads = Uploader.Uploads as any;

const LazyHistory = lazy(() => import(/* webpackChunkName: "History" */ "./sidebars/history"));
const LazyChildCardsPanelGrouped = lazy(
  () =>
    import(
      /* webpackChunkName: "ChildCardsPanelGrouped" */ "../workflows/WorkflowSidebarContentGrouped"
    )
);

type EditCardProps = {
  card: Card;
  root: Root;
  onSaveContent: (c: string) => any;
  onReceiveFile: (f: any) => any;
  onClose: () => void;
  focusProps: FocusProps;
  selectContent?: SelectContent | null;
  uploads: ReactNode;
};
const EditCard = (props: EditCardProps) => {
  const {card, focusProps, onClose, onReceiveFile, root, selectContent, onSaveContent, uploads} =
    props;
  const cardId = card.$meta.get("cardId", false);

  const handleCardChange = (data: any) => {
    const {assignee, deckId, ...rest} = data;
    if (!cardId) return;
    if (deckId) {
      return moveCardsToDeck([cardId], deckId);
    } else {
      if (assignee !== undefined) rest.assigneeId = assignee;
      // not using useMutation here, as it prevents parallel prop changes
      api.mutate.cards.update({id: cardId, ...rest});
    }
  };

  const passedFocusProps = {
    ...focusProps,
    setActive: (opts?: {editorState?: any}) => {
      cardEditPersistStore.clearStoredValue(card);
      onClose();
      focusProps.setActive(opts);
    },
  };

  return (
    <>
      {uploads}
      <EditCardContainerContent
        card={card}
        onClose={onClose}
        selectContent={selectContent}
        persistStore={cardEditPersistStore}
        contentKey={cardId}
        onSaveContent={onSaveContent}
        onCardChange={handleCardChange}
        focusProps={passedFocusProps}
        onReceiveFile={onReceiveFile}
      />
      <CardDetailFrameAboveBottom card={card} project={card.deck?.project} root={root} canModify />
    </>
  );
};

const ModifyCoverImage = ({
  card,
  parentCard,
  root,
}: {
  root: Root;
  card: Card;
  parentCard: Card | null;
}) => {
  const {coverFile, coverFileCardMeta, cardId} = getCoverFileWithParent(root, card, parentCard);
  const handleSetCoverFile = (
    coverFileId: null | FileId,
    coverFileModifier: CoverFileModifier | null
  ) => {
    return api.mutate.cards.update({id: cardId, coverFileId, coverFileModifier});
  };
  return (
    <DropDownForChild
      setChildProps={({isOpen}: any) => ({hidden: isOpen})}
      renderOverlay={({close, ...props}: any) => (
        <ArrowOverlay bg="white" arrowSize="xs" allowMouseEventBubbling {...props}>
          <CoverImageOptions
            onClose={close}
            onSetCoverFile={handleSetCoverFile}
            file={coverFile}
            cardMeta={coverFileCardMeta}
            isCurrent
            withChildCards={hasChildCards(card)}
          />
        </ArrowOverlay>
      )}
      overlayProps={{placement: "right", distanceFromAnchor: 10}}
    >
      <TooltipForChild tooltip="Update this Cover Image">
        <DSIconButton size="sm" variant="secondary" icon={<DSIconMedia />} />
      </TooltipForChild>
    </DropDownForChild>
  );
};

const CoverImage = ({coverInfo}: {coverInfo: CoverInfo}) => {
  const mod = coverInfo.modifier;
  return (
    <Box
      absolute
      top="0"
      left="0"
      width="100%"
      style={{
        aspectRatio: "2.5",
        borderRadius: "0 0 10px 10px",
        boxShadow: "0 1px 0 rgba(0 0 0 / 0.1)",
      }}
      overflow="hidden"
      className={uiStyles.hideContainer}
      colorTheme={coverInfo.type === "white" ? "white" : "gray750"}
      bg="foreground"
    >
      <CdxCropImg
        src={coverInfo.url}
        width={580}
        height={580 / 2.5}
        className={css({position: "absolute", inset: "0"})}
        imgClassName={css({width: "100%", height: "100%"})}
        offset={
          mod && "bigCardOffsetX" in mod ? [mod.bigCardOffsetX, mod.bigCardOffsetY] : [50, 33]
        }
      />
    </Box>
  );
};

type ViewCardProps = {
  card: Card;
  root: Root;
  canModify: boolean | null;
  focusProps: FocusProps;
  location: Location;
  history: History;
  setForceThreadType: Dispatch<SetStateAction<"review" | "block" | null>>;
  onOpenEditPanel: (maybeContentEvent: any) => void;
  sidePanelKey: string | null;
  arenaCtx: ArenaCtx;
  onContentClick: (e: any) => void;
  onSaveContent: (c: string) => void;
  uploads: ReactNode;
  onCardDone?: () => Promise<unknown>;
  cardOrder?: CardOrder | null;
  parentCard: Card | null;
  coverInfo: CoverInfo | null;
};
const ViewCard = (props: ViewCardProps) => {
  const {
    card,
    root,
    canModify,
    focusProps,
    location,
    history,
    setForceThreadType,
    onOpenEditPanel,
    arenaCtx,
    onContentClick,
    onSaveContent,
    uploads,
    onCardDone,
    cardOrder,
    parentCard,
    coverInfo,
  } = props;
  const withDeps =
    process.env.REACT_APP_MODE !== "open" && root.account.dependenciesEnabled && !card.isDoc;
  const cardId = card.$meta.get("cardId", null);
  const backlinkCards = cardId
    ? root.account.$meta
        .find("cards", {cardReferences: {op: "has", value: cardId} as any})
        .filter((c: any) => c.$meta.isLoaded)
    : [];

  return (
    <>
      {canModify && <DropChildCardArea card={card} />}
      <Workflow
        card={card}
        root={root}
        canModify={canModify}
        onOpenChildCards={() => openSidePanel(history, location, "childCards")}
        onOpenConvoPanel={() => openSidePanel(history, location, "comments")}
        onCreateConvo={(convo: any) => {
          openSidePanel(history, location, "comments");
          setForceThreadType(convo);
        }}
        focusProps={focusProps}
        onCardDone={onCardDone}
        coverInfo={coverInfo}
      />
      {coverInfo && <CoverImage coverInfo={coverInfo} />}
      {uploads}
      <Col bg="foreground" flex="auto" minHeight="0">
        <ViewCardContainerContent
          card={card}
          canModify={canModify}
          onOpenEditPanel={onOpenEditPanel}
          onContentClick={onContentClick}
          project={card.deck?.project}
          persistStore={cardEditPersistStore}
          onSaveContent={onSaveContent}
          coverInfo={coverInfo}
          modifyCoverImageButton={
            canModify &&
            coverInfo && <ModifyCoverImage root={root} card={card} parentCard={parentCard} />
          }
          belowContent={<CardEmbeds card={card} canModify={canModify} />}
          bottom={
            <OpenCardStatusRow
              card={card}
              root={root}
              arenaCtx={arenaCtx}
              history={history}
              canModify={canModify}
              onOpenEditPanel={onOpenEditPanel}
              cardOrder={cardOrder}
            />
          }
        />
        <CardDetailFrameAboveBottom
          card={card}
          project={card.deck?.project}
          allowDeps={withDeps}
          backlinkCards={backlinkCards}
          root={root}
          canModify={canModify}
        />
      </Col>
    </>
  );
};

const category = "Card detail view";
const BottomButtons = (props: {
  card: Card;
  root: Root;
  canModify: boolean | null;
  status: string;
  onClose: () => void;
  arenaCtx: ArenaCtx;
}) => {
  const {card, root, canModify, status, onClose, arenaCtx} = props;
  const canBookmark = hasPermissionToBookmarkCard(root);
  const canWatch = hasPermissionToWatchCard(root);
  const canDoc = canModify && !hasChildCards(card);
  const mayGuardCard = hasPermissionToGuardCard(root, card);

  return (
    <Col mt="auto" sp="4px">
      {canBookmark && <BookmarkBottomButton root={root} card={card} />}
      {canDoc && <DocCardBottomButton card={card} status={status} />}
      {canWatch && <SubscribeBottomButton card={card} root={root} />}
      {canModify && (
        <ArchiveOrDeleteBottomButton
          card={card}
          onClose={onClose}
          status={status}
          category={category}
          disabledHint={mayGuardCard ? null : "Only Guardians may archive a card"}
          arenaCtx={arenaCtx}
        />
      )}
    </Col>
  );
};
type OpenCardProps = {
  card: Card;
  root: Root;
  onClose: () => void;
  arenaCtx: any;
  cardOrder?: CardOrder | null;
} & Pick<UnifiedCardContainerOpts<any>, "location" | "history">;
const OpenCard = (props: OpenCardProps) => {
  const {location, history, onClose, root, card, arenaCtx, cardOrder} = props;
  const [attBlingRef, setAttBling] = useBling();
  const [mainView, setMainView] = useState<"view" | "edit">("view");
  const [forceThreadType, setForceThreadType] = useState<null | "review" | "block">(null);
  const [selectContent, setSelectContent] = useState(null);
  useEffect(() => {
    if (selectContent && mainView === "view") setSelectContent(null);
  }, [selectContent, mainView]);

  const clickToEdit = root.loggedInUser?.enableClickToEditCards;

  const handleContentClick = (e: any) => {
    if (!clickToEdit) return;
    if (isSelectionActive()) return;
    setMainView("edit");
    if (e && e.nativeEvent && e.nativeEvent.$cdxEvent && e.nativeEvent.$cdxEvent.position) {
      setSelectContent(e.nativeEvent.$cdxEvent.position);
      e.stopPropagation(); // since there are two listeners: parent/child
    }
  };

  const canModify =
    process.env.REACT_APP_MODE !== "open" && hasPermissionToModifyCard({root, card});
  const meId = root.loggedInUser?.$meta.get("id", null);
  const cardId = card.$meta.get("cardId", null);
  useEffect(() => {
    // if card changes, reset main view and force thread type
    if (cardId)
      return () => {
        setForceThreadType(null);
        setMainView("view");
      };
  }, [cardId]);
  const [justDone, setJustDone] = useState(false);
  const rawStatus = getStatusForCard(card);
  const status = justDone ? "done" : rawStatus;
  const notifications = useNotifications() as any;
  const showConvos =
    process.env.REACT_APP_MODE !== "open" ||
    (process.env.REACT_APP_MODE === "open" && card.deck && card.deck.project.commentsArePublic);

  const withChildCards = hasChildCards(card);
  const parentCard = getParentCard(card);
  const coverInfo = getCoverInfoWithParent(root, card, parentCard);
  const withParentPill = parentCard && parentCard.$meta.isLoaded;
  const handlePillClick = () => {
    openSidePanel(history, location, "childCards", sidePanelKey === "childCards");
  };

  const showChildCards = () => {
    if (withChildCards) return true;
    if (parentCard && parentCard.$meta.isLoaded) return true;
    if (process.env.REACT_APP_MODE !== "open") return true;
    return false;
  };

  const handleCloseSidePanel = () => openSidePanel(history, location, null, true);
  const handlePropChange = (data: any) => {
    const {assignee, dueDate, ...rest} = data;
    if (assignee !== undefined) rest.assigneeId = assignee;
    if (dueDate !== undefined) rest.dueDate = dueDate && dayToDateStr(dueDate);
    return api.mutate.cards.update({id: cardId, ...rest});
  };
  const handleMediaUpload = (fileData: any) =>
    api.mutate.cards.addFile({cardId: cardId, fileData, userId: meId}).then((res: any) => {
      if (!focusActive) setAttBling("+1");
      return res;
    });
  const handleAttachmentDelete = (attachmentId: string) =>
    api.mutate.attachments.delete({cardId, id: attachmentId}).then(() => setAttBling("-1"));
  const handleAttachmentEdit = ({id, content}: any) => api.mutate.attachments.update({id, content});

  const canSetCoverFile = () => {
    if (!parentCard) return true;
    if (root.account.allowInheritHeroCover === false) return true;
    return !Boolean(parentCard?.coverFile);
  };

  const handleSetCoverFile = canSetCoverFile()
    ? (coverFileId: string, coverFileModifier: any) => {
        return api.mutate.cards.update({id: cardId, coverFileId, coverFileModifier});
      }
    : null;

  const {dropAreaProps, ongoingUploads, onReceiveFile} = useUpload({
    id: `attachment-${cardId}`,
    multiple: true,
    affectsQuota: true,
    onUpload: canModify && handleMediaUpload,
  });
  useFilePaster(onReceiveFile);

  const handleSaveContent = (content: string) => api.mutate.cards.update({id: cardId, content});

  const [focusEl, focusActive, setFocusActive] = useFocusMode({
    card,
    root,
    onReceiveFile,
    canModify,
    location,
    history,
    uploadInfo: {ongoingUploads, dropAreaProps},
    onSubmit: handleSaveContent,
    contentKey: card.$meta.get("cardId", false),
    getInitialContent: () =>
      lexicalSerializer.fromJSON(cardEditPersistStore.getStoredValue(card)) ||
      card.$meta.get("content", false),
  });
  const focusProps: FocusProps = {active: focusActive, setActive: setFocusActive};

  const attachmentCount = card.$meta.count("attachments");

  const [order] = useLocalStorageState("default-attachment-order", "asc");
  const imgKeysAndInfos: any[] = [];
  card.attachments.forEach(({file, id, title}: any) => {
    const url = file.$meta.get("url", null);
    const {width, height} = file.meta;
    if (url && canDisplayAsImage(file)) imgKeysAndInfos.push([id, {url, width, height, title}]);
  });
  if (order === "asc") imgKeysAndInfos.reverse();
  useImageViewerInfo({key: `att-${card.$meta.get("cardId", null)}`, imgKeysAndInfos});

  const cardNodeRef = useRef(null);
  const {node: animNode, onStart: onCardDone} = useDoneAnim(cardNodeRef);

  const me = root.loggedInUser;
  const allowAnimation = !me || !me.disableAnimations;

  const handleCardDone = () => {
    if (!allowAnimation) return Promise.resolve();
    setJustDone(true);
    return onCardDone().then(() => {
      waitForResult(
        () => getStatusForCard(api.getModel({modelName: "card", id: cardId})!),
        () => setTimeout(() => setJustDone(false), 200)
      );
    });
  };

  const {Container, containerProps, sidePanelKey} = useUnifiedCardContainer({
    location,
    history,
    onClose,
    category,
    sidePanels: [
      ...(process.env.REACT_APP_MODE !== "open"
        ? [
            {
              key: "props",
              content: (
                <PropEdit
                  card={card}
                  onChange={handlePropChange}
                  onClose={handleCloseSidePanel}
                  root={root}
                />
              ),
              button: {
                comp: PropPanelButton,
              },
            },
          ]
        : []),
      {
        key: "attachments",
        content: (
          <Attachments
            card={card}
            root={root}
            onClose={handleCloseSidePanel}
            onUpload={handleMediaUpload}
            onDelete={handleAttachmentDelete}
            onEdit={handleAttachmentEdit}
            onSetCoverFile={handleSetCoverFile}
            canModify={canModify}
            location={location}
            history={history}
          />
        ),
        button: {
          comp: AttachmentPanelButton,
          props: {
            tooltip: canModify ? "View and upload attachments" : "View attachments",
            variant: attachmentCount > 0 ? "nonEmpty" : "default",
            blingRef: attBlingRef,
          },
        },
      },
      ...(showConvos
        ? [
            {
              key: "comments",
              content: (
                <Conversations
                  card={card}
                  root={root}
                  notifications={notifications}
                  canModify={canModify}
                  forceThreadType={forceThreadType}
                  location={location}
                  history={history}
                  // onClose={handleCloseSidePanel}
                  onEditCardClick={() => setMainView("edit")}
                  status={status}
                  onReceiveFile={onReceiveFile}
                  onUnmount={() => setForceThreadType(null)}
                  onForceThreadCreated={() => setForceThreadType(null)}
                  onCardDone={handleCardDone}
                />
              ),
              button: {
                comp: ConvoPanelButton,
                props: {card, root, notifications},
              },
            },
          ]
        : []),
      ...(process.env.REACT_APP_MODE !== "open"
        ? [
            {
              key: "history",
              content: (
                <LazyHistory
                  card={card}
                  root={root}
                  notifications={notifications}
                  canModify={canModify}
                  isWorkflowItem={false}
                />
              ),
              button: {
                comp: HistoryPanelButton,
                props: {notifications, card, root},
              },
            },
          ]
        : []),
      ...(showChildCards()
        ? [
            {
              key: "childCards",
              content: (
                <LazyChildCardsPanelGrouped
                  card={card}
                  root={root}
                  canModify={canModify}
                  arenaCtx={arenaCtx}
                  history={history}
                  notifications={notifications}
                />
              ),
              button: {
                comp: ChildCardPanelButton,
                props: {card, root},
              },
            },
          ]
        : []),
    ],
    sideBottom: (
      <BottomButtons
        card={card}
        root={root}
        canModify={canModify}
        status={status}
        onClose={onClose}
        arenaCtx={arenaCtx}
      />
    ),
  });

  const uploads = sidePanelKey !== "attachments" && (
    <ShowUploads
      ongoingUploads={ongoingUploads}
      className={cx(dsStyles.px["12px"], dsStyles.py["8px"])}
    />
  );

  usePropOnHoverChange({
    type: "BIG_CARD",
    updateFn:
      process.env.REACT_APP_MODE !== "open"
        ? ({effort, priority}: any) => {
            if (!canModify) {
              messenger.send("You don't have permissions to modify this card");
            } else {
              return api.mutate.cards.update({id: cardId, effort, priority});
            }
          }
        : undefined,
  });

  const nodeRef = useRef(null);

  const {cardStyles, containerEvents} = useBigCardAnimation({
    disabled: !allowAnimation,
    nodeRef,
  });

  return (
    <Container
      contextName="CardDetail"
      uploadArea={
        canModify && (
          <Uploader.DropArea
            {...dropAreaProps}
            label="Attach file to this card"
            labelClassName={cardContainerStyles.stickyTop}
          />
        )
      }
      withParentPill={Boolean(withParentPill)}
      withCoverImage={Boolean(coverInfo)}
      {...containerProps}
    >
      <animated.div
        className={css({display: "flex", flexDir: "column", minHeight: "0", sp: "12px"})}
        style={cardStyles}
        {...containerEvents}
        ref={nodeRef}
      >
        {process.env.REACT_APP_MODE === "open" ? (
          <PublicTopArea card={card} arenaCtx={arenaCtx} />
        ) : (
          <NonPublicTopArea
            card={card}
            root={root}
            arenaCtx={arenaCtx}
            canModify={canModify}
            category={category}
          />
        )}
        {withParentPill && (
          <CardParentPill
            parent={parentCard}
            onClick={handlePillClick}
            childCardPanelOpen={sidePanelKey === "childCards"}
            arenaCtx={arenaCtx}
            root={root}
          />
        )}
        <CardDetailFrame
          card={card}
          childCardInfo={withChildCards ? getChildCardInfo(card, root.account) : null}
          type="BIG_CARD"
          onChildCardBarClick={() => openSidePanel(history, location, "childCards")}
          status={status}
          root={root}
          ref={cardNodeRef}
          canModify={canModify}
          withCoverImage={Boolean(coverInfo)}
          transparentBg={mainView === "view"}
        >
          <Col minHeight="0" flex="auto" relative>
            {mainView === "view" ? (
              <ViewCard
                card={card}
                root={root}
                canModify={canModify}
                focusProps={focusProps}
                location={location}
                history={history}
                setForceThreadType={setForceThreadType}
                onOpenEditPanel={() => setMainView("edit")}
                sidePanelKey={sidePanelKey}
                arenaCtx={arenaCtx}
                onContentClick={handleContentClick}
                onSaveContent={handleSaveContent}
                uploads={uploads}
                onCardDone={handleCardDone}
                cardOrder={cardOrder}
                parentCard={parentCard}
                coverInfo={coverInfo}
              />
            ) : (
              <EditCard
                card={card}
                root={root}
                onReceiveFile={onReceiveFile}
                onClose={() => setMainView("view")}
                focusProps={focusProps}
                selectContent={selectContent}
                onSaveContent={handleSaveContent}
                uploads={uploads}
              />
            )}
          </Col>
        </CardDetailFrame>
        {animNode}
        {focusEl}
      </animated.div>
    </Container>
  );
};

export default OpenCard;
